import React, { useMemo, useState } from 'react';
import { CoachNote } from '@court-sync/shared';
import { Card, CardHeader, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '@/components/ui';
import { PaginatedList } from '../ui/PaginatedList';
import type { PaginationState } from '../ui/PaginatedTable';
import { COACH_NOTES_PAGE_SIZE } from '@/services/coachNotesService';
import {
  MessageSquare,
  Plus,
  User,
  Calendar,
  Lock,
  Eye,
  Target,
  TrendingUp,
  MessageCircle,
  Users,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';

export type CoachPathwaySkillOption = {
  programId: string;
  programName: string;
  skillName: string;
  completed: boolean;
};

export type CoachNoteCreatePayload = {
  content: string;
  category: CoachNote['category'];
  isPrivate: boolean;
  programId?: string;
  programSkillName?: string;
  rating?: number;
  skillAchieved?: boolean;
};

interface CoachNotesPanelProps {
  notes: CoachNote[];
  /** Server-driven pagination (newest notes on page 1). Omit only in tests or legacy mocks. */
  notesPagination?: PaginationState | null;
  /** Called when the user changes page (parent should refetch from the API). */
  onNotesPageChange?: (page: number) => void;
  /** True while a page of notes is loading from the API. */
  notesLoading?: boolean;
  onAddNote?: (note: CoachNoteCreatePayload) => void;
  isCoach?: boolean;
  className?: string;
  /** When set, Progress notes can optionally target a pathway skill */
  pathwaySkillOptions?: CoachPathwaySkillOption[];
  /** Resolve program display name for notes that store programId */
  programNameById?: Record<string, string>;
  /** True while pathway skill options are being fetched (shows loading under Category when Progress is selected) */
  pathwayOptionsLoading?: boolean;
  /** Shown when Progress is selected but there is no pathway dropdown yet (tier, no program bookings, no target skills, etc.) */
  pathwayEmptyHint?: string | null;
  /** Called when user clicks "See details" on a pathway skill note (e.g. switch to Progress tab) */
  onSeePathwayDetails?: () => void;
}

interface NoteCardProps {
  note: CoachNote;
  isCoach: boolean;
  programNameById?: Record<string, string>;
  onSeePathwayDetails?: () => void;
}

const PATHWAY_KEY_SEP = '|||';

function encodePathwayKey(programId: string, skillName: string): string {
  return `${programId}${PATHWAY_KEY_SEP}${skillName}`;
}

const CATEGORY_CONFIG: Record<CoachNote['category'], { label: string; icon: React.ReactNode; color: string }> = {
  general: {
    label: 'General',
    icon: <MessageCircle className="w-4 h-4" />,
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  },
  progress: {
    label: 'Progress',
    icon: <TrendingUp className="w-4 h-4" />,
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  },
  behavior: {
    label: 'Behavior',
    icon: <User className="w-4 h-4" />,
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  },
  goal: {
    label: 'Goal',
    icon: <Target className="w-4 h-4" />,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  },
  parent_communication: {
    label: 'Parent Communication',
    icon: <Users className="w-4 h-4" />,
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  },
};

const formatDate = (date: Date) => {
  const d = new Date(date);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
};

const NoteCard: React.FC<NoteCardProps> = ({ note, isCoach, programNameById, onSeePathwayDetails }) => {
  const categoryConfig = CATEGORY_CONFIG[note.category];
  const programLabel =
    note.programId && programNameById?.[note.programId] ? programNameById[note.programId] : note.programId;

  return (
    <div
      className={cn(
        'border border-gray-200 dark:border-gray-700 rounded-lg p-4',
        note.isPrivate && 'border-l-4 border-l-orange-400'
      )}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={cn('p-1.5 rounded-full', categoryConfig.color)}>{categoryConfig.icon}</div>
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-white">{note.coachName}</div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <Calendar className="w-3 h-3" />
              {formatDate(note.createdAt)}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          <Badge className={categoryConfig.color}>{categoryConfig.label}</Badge>
          {note.programId && note.programSkillName && note.rating != null && (
            <Badge variant="outline" className="text-xs font-normal">
              Pathway: {programLabel ?? 'Program'} · {note.programSkillName} · {note.rating}/5
            </Badge>
          )}
          {note.skillAchieved && (
            <Badge className="bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200 gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
              Achieved
            </Badge>
          )}
          {note.isPrivate && isCoach && (
            <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400">
              <Lock className="w-3 h-3" />
              <span>Private</span>
            </div>
          )}
          {!note.isPrivate && note.readByParent && (
            <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <Eye className="w-3 h-3" />
              <span>Read</span>
            </div>
          )}
        </div>
      </div>

      {note.content.trim() ? (
        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{note.content}</p>
      ) : note.programId ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm italic">No additional note text</p>
      ) : null}

      {note.programId && note.programSkillName && note.rating != null && onSeePathwayDetails && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onSeePathwayDetails}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 underline-offset-2 hover:underline"
          >
            See details
          </button>
        </div>
      )}
    </div>
  );
};

export const CoachNotesPanel: React.FC<CoachNotesPanelProps> = ({
  notes,
  notesPagination = null,
  onNotesPageChange,
  notesLoading = false,
  onAddNote,
  isCoach = false,
  className,
  pathwaySkillOptions = [],
  programNameById = {},
  pathwayOptionsLoading = false,
  pathwayEmptyHint = null,
  onSeePathwayDetails,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newNote, setNewNote] = useState({
    content: '',
    category: 'general' as CoachNote['category'],
    isPrivate: false,
  });
  const [pathwayKey, setPathwayKey] = useState('');
  const [pathwayRating, setPathwayRating] = useState<number | ''>('');
  const [skillAchieved, setSkillAchieved] = useState(false);

  const showPathwayUi =
    isCoach && pathwaySkillOptions.length > 0 && newNote.category === 'progress';

  const showPathwayProgressContext =
    isCoach && newNote.category === 'progress' && (pathwayOptionsLoading || showPathwayUi || pathwayEmptyHint);

  const resetPathwayFields = () => {
    setPathwayKey('');
    setPathwayRating('');
    setSkillAchieved(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!onAddNote) return;

    const pathwayActive = showPathwayUi && pathwayKey !== '';

    if (pathwayActive) {
      const r = pathwayRating === '' ? NaN : Number(pathwayRating);
      if (!Number.isInteger(r) || r < 1 || r > 5) {
        return;
      }
      const sep = pathwayKey.indexOf(PATHWAY_KEY_SEP);
      if (sep === -1) return;
      const programId = pathwayKey.slice(0, sep);
      const programSkillName = pathwayKey.slice(sep + PATHWAY_KEY_SEP.length);
      onAddNote({
        content: newNote.content.trim(),
        category: 'progress',
        isPrivate: newNote.isPrivate,
        programId,
        programSkillName,
        rating: r,
        skillAchieved,
      });
    } else {
      if (!newNote.content.trim()) return;
      onAddNote({
        content: newNote.content.trim(),
        category: newNote.category,
        isPrivate: newNote.isPrivate,
      });
    }

    setNewNote({ content: '', category: 'general', isPrivate: false });
    resetPathwayFields();
    setShowAddForm(false);
  };

  const pathwaySubmitReady =
    pathwayRating !== '' && Number.isInteger(Number(pathwayRating)) && Number(pathwayRating) >= 1 && Number(pathwayRating) <= 5;

  const canSubmit = showPathwayUi && pathwayKey !== ''
    ? pathwaySubmitReady
    : !!newNote.content.trim();

  const visibleNotes = isCoach ? notes : notes.filter((n) => !n.isPrivate);

  const listPagination: PaginationState | null = useMemo(() => {
    if (notesPagination) return notesPagination;
    if (visibleNotes.length === 0) return null;
    return {
      page: 1,
      limit: COACH_NOTES_PAGE_SIZE,
      total: visibleNotes.length,
      totalPages: 1,
    };
  }, [notesPagination, visibleNotes.length]);

  const notesTotalCount = notesPagination?.total ?? visibleNotes.length;

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Coach Notes
            {notesTotalCount > 0 && <Badge variant="secondary">{notesTotalCount}</Badge>}
          </h3>
          {isCoach &&
            onAddNote &&
            (notesLoading || visibleNotes.length > 0 || showAddForm) && (
              <Button
                size="sm"
                variant={showAddForm ? 'outline' : 'default'}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? (
                  'Cancel'
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Note
                  </>
                )}
              </Button>
            )}
        </div>
      </CardHeader>
      <CardContent>
        {showAddForm && isCoach && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <select
                  value={newNote.category}
                  onChange={(e) => {
                    const cat = e.target.value as CoachNote['category'];
                    setNewNote({ ...newNote, category: cat });
                    if (cat !== 'progress') resetPathwayFields();
                  }}
                  className="w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>

              {showPathwayProgressContext && pathwayOptionsLoading && (
                <p className="text-sm text-gray-500 dark:text-gray-400">Loading pathway skills…</p>
              )}

              {showPathwayProgressContext &&
                !pathwayOptionsLoading &&
                !showPathwayUi &&
                pathwayEmptyHint && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{pathwayEmptyHint}</p>
                )}

              {showPathwayUi && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Pathway skill (optional)
                  </label>
                  <select
                    value={pathwayKey}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPathwayKey(v);
                      if (!v) {
                        setPathwayRating('');
                        setSkillAchieved(false);
                      } else {
                        const opt = pathwaySkillOptions.find(
                          (o) => encodePathwayKey(o.programId, o.skillName) === v
                        );
                        setSkillAchieved(opt?.completed ?? false);
                      }
                    }}
                    className="w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">General progress (no pathway skill)</option>
                    {pathwaySkillOptions.map((o) => (
                      <option key={encodePathwayKey(o.programId, o.skillName)} value={encodePathwayKey(o.programId, o.skillName)}>
                        {o.programName} — {o.skillName}
                        {o.completed ? ' (achieved)' : ''}
                      </option>
                    ))}
                  </select>
                  {pathwayKey !== '' && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Rating (required)
                        </label>
                        <select
                          value={pathwayRating === '' ? '' : String(pathwayRating)}
                          onChange={(e) =>
                            setPathwayRating(e.target.value === '' ? '' : (parseInt(e.target.value, 10) as 1 | 2 | 3 | 4 | 5))
                          }
                          className="w-full pl-3 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">Select 1–5</option>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={skillAchieved}
                          onChange={(e) => setSkillAchieved(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-0.5"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Mark skill as achieved (updates pathway checklist)
                        </span>
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note</label>
                <textarea
                  value={newNote.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                  placeholder={
                    showPathwayUi && pathwayKey !== ''
                      ? 'Optional details…'
                      : 'Write your note here...'
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newNote.isPrivate}
                    onChange={(e) => setNewNote({ ...newNote, isPrivate: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1">
                    <Lock className="w-3.5 h-3.5" />
                    Private (coaches only)
                  </span>
                </label>
                <Button type="submit" disabled={!canSubmit}>
                  Save Note
                </Button>
              </div>
            </div>
          </form>
        )}

        {notesLoading && visibleNotes.length === 0 ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400" />
          </div>
        ) : visibleNotes.length === 0 && !notesLoading ? (
          <DashboardEmptyStateCard>
            <DashboardEmptyStateIcon icon={MessageSquare} />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No coach notes yet</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {isCoach
                ? 'Add a note to track progress, behaviour, goals, or parent communication.'
                : 'Notes from coaches will appear here.'}
            </p>
            {isCoach && onAddNote && !showAddForm && (
              <Button type="button" size="sm" onClick={() => setShowAddForm(true)}>
                <Plus className="w-4 h-4 mr-1" aria-hidden />
                Add note
              </Button>
            )}
          </DashboardEmptyStateCard>
        ) : (
          <PaginatedList<CoachNote>
            data={visibleNotes}
            pagination={listPagination}
            loading={notesLoading}
            onPageChange={(page) => onNotesPageChange?.(page)}
            getItemKey={(note) => note.id}
            renderItem={(note) => (
              <NoteCard
                note={note}
                isCoach={isCoach}
                programNameById={programNameById}
                onSeePathwayDetails={onSeePathwayDetails}
              />
            )}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default CoachNotesPanel;
